'use client';

import { Stack } from '@mui/material';
import type { ReactNode } from 'react';

import { Button } from '@campaign-manager/react/components';

export interface WizardActionBarProps {
  /** Label for the "previous step" button */
  prevStepButtonLabel?: string;
  /** Label for the "next step" button */
  nextStepButtonLabel: string;
  /** Optional label for an alternative primary button (e.g., "Save & Activate") */
  altPrimaryButtonLabel?: string;

  /** Callback when "next step" button is clicked */
  onNextStepButtonClick?: () => void;
  /** Callback when "previous step" button is clicked */
  onPrevStepButtonClick?: () => void;
  /** Callback when alternative primary button is clicked */
  onAltPrimaryButtonClick?: () => void;

  /** Whether the "previous step" button is disabled */
  prevStepButtonDisabled?: boolean;
  /** Whether the "next step" button is disabled */
  nextStepButtonDisabled?: boolean;
  /** Whether the alternative primary button is disabled */
  altPrimaryButtonDisabled?: boolean;

  /** Optional additional content to render on the left side */
  leftContent?: ReactNode;
}

/**
 * WizardActionBar - Navigation buttons for wizard layouts
 *
 * This component is designed to work within the WizardLayout action bar slot.
 * It doesn't use position: fixed - the positioning is handled by the parent
 * WizardLayout grid.
 *
 * The action bar provides:
 * - Previous/Back navigation
 * - Next/Continue navigation
 * - Optional alternative primary action (e.g., "Save & Activate")
 * - Optional left-side content for additional actions
 */
export default function WizardActionBar({
  nextStepButtonLabel,
  prevStepButtonLabel,
  altPrimaryButtonLabel,
  onNextStepButtonClick,
  onPrevStepButtonClick,
  onAltPrimaryButtonClick,
  prevStepButtonDisabled,
  nextStepButtonDisabled,
  altPrimaryButtonDisabled,
  leftContent,
}: WizardActionBarProps) {
  return (
    <Stack
      direction="row"
      sx={{
        gap: 2,
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      {/* Left side - optional content */}
      <div>{leftContent}</div>

      {/* Right side - action buttons */}
      <Stack
        direction="row"
        sx={{
          gap: 2,
          alignItems: 'center',
        }}
      >
        {prevStepButtonLabel && onPrevStepButtonClick && (
          <Button
            type="button"
            onClick={onPrevStepButtonClick}
            disabled={prevStepButtonDisabled}
            size="medium"
            color="secondary"
            variant="text"
          >
            {prevStepButtonLabel}
          </Button>
        )}
        <Button
          type="button"
          onClick={onNextStepButtonClick}
          disabled={nextStepButtonDisabled}
          size="medium"
          variant="contained"
          color={altPrimaryButtonLabel ? 'inherit' : 'primary'}
        >
          {nextStepButtonLabel}
        </Button>
        {altPrimaryButtonLabel && (
          <Button
            type="button"
            onClick={onAltPrimaryButtonClick}
            disabled={altPrimaryButtonDisabled}
            size="medium"
            variant="contained"
            color="primary"
          >
            {altPrimaryButtonLabel}
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
